/**
 * useFieldStateManager Hook
 *
 * Manages field state tracking for additional questions:
 * - Dirty state tracking for unsaved changes
 * - Initial field state management
 * - Field change detection
 * - Clean state restoration after form submission
 */

import * as React from 'react';
import { IFormField } from '@ViewModels';
import { FieldStateManager } from '../types';

/**
 * Custom hook for managing field state and dirty tracking
 *
 * @param fields - Current array of form fields
 * @param isClone - Whether this is a cloned form (affects saved state detection)
 * @returns FieldStateManager with state and utility functions
 */
export const useFieldStateManager = (fields: IFormField<string>[], _isClone = false): FieldStateManager => {
	// Custom dirty tracking state
	const [dirtyFields, setDirtyFields] = React.useState<Set<string>>(new Set());

	// Track initial field states to prevent false dirty marking
	const [initialFieldStates, setInitialFieldStates] = React.useState<Map<string, IFormField<string>>>(new Map());

	// True until the first non-empty `fields` array is captured as the initial baseline.
	// While true, `markFieldDirty` is a no-op so async render-time effects can't spuriously
	// mark fields dirty before the user has had a chance to edit them. Container code is
	// expected to call `resetDirtyState()` + `updateInitialStates()` from its save mutation's
	// onSuccess to re-baseline after a save (a previous version used a `fields.every(_type)`
	// heuristic in an effect, but that fired on every label keystroke and wiped the dirty set).
	const [isInitialSetup, setIsInitialSetup] = React.useState(true);

	// Capture the initial baseline once when fields first populate.
	React.useEffect(() => {
		if (initialFieldStates.size === 0 && fields.length > 0) {
			const newStates = new Map<string, IFormField<string>>();
			fields.forEach(field => {
				newStates.set(field.id, { ...field });
			});
			setInitialFieldStates(newStates);
			setIsInitialSetup(false);
		}
	}, [fields, initialFieldStates.size]);

	// Helper function to check if a field is dirty (has unsaved changes)
	const isFieldDirty = React.useCallback(
		(fieldId: string, _fieldIndex: number): boolean => {
			// Check if the field is in our custom dirty tracking
			return dirtyFields.has(fieldId);
		},
		[dirtyFields]
	);

	// Use ref to track if initial setup is complete for synchronous access
	const isInitialSetupRef = React.useRef(isInitialSetup);
	isInitialSetupRef.current = isInitialSetup;

	// Function to mark a field as dirty
	const markFieldDirty = React.useCallback((fieldId: string) => {
		if (!isInitialSetupRef.current) {
			setDirtyFields(prev => {
				const newDirtyFields = new Set(prev);
				newDirtyFields.add(fieldId);
				return newDirtyFields;
			});
		}
	}, []);

	// Function to check if a field has actually changed from its initial state.
	// `options` is normalized to `[]` on both sides because useFieldTypeChange runs a
	// mount-time normalization that converts a String field's `undefined` options to `[]`
	// (and calls onUpdateField). Treating those representations as different produces a
	// spurious dirty mark on initial load for any String field the server stored without
	// an `options` key.
	const hasFieldChanged = React.useCallback(
		(field: IFormField<string>): boolean => {
			const initialField = initialFieldStates.get(field.id);
			if (!initialField) return true; // New field, consider it changed

			return (
				field.label !== initialField.label ||
				field.isOptional !== initialField.isOptional ||
				field.fieldType !== initialField.fieldType ||
				JSON.stringify(field.options ?? []) !== JSON.stringify(initialField.options ?? [])
			);
		},
		[initialFieldStates]
	);

	// Function to conditionally mark field as dirty based on actual changes
	const markFieldDirtyIfChanged = React.useCallback(
		(field: IFormField<string>) => {
			const isActuallyDirty = hasFieldChanged(field);

			if (isActuallyDirty && !isInitialSetupRef.current) {
				markFieldDirty(field.id);
			}
		},
		[hasFieldChanged, markFieldDirty]
	);

	// Function to reset all dirty state
	const resetDirtyState = React.useCallback(() => {
		setDirtyFields(new Set());
		// End initial setup when dirty state is reset (simulates form completion)
		if (isInitialSetupRef.current) {
			setIsInitialSetup(false);
			isInitialSetupRef.current = false; // Update ref immediately for synchronous access
		}
	}, []);

	// Function to update initial states (useful when fields are programmatically updated)
	const updateInitialStates = React.useCallback((updatedFields: IFormField<string>[]) => {
		const updatedInitialStates = new Map<string, IFormField<string>>();
		updatedFields.forEach(field => {
			updatedInitialStates.set(field.id, { ...field });
		});
		setInitialFieldStates(updatedInitialStates);
	}, []);

	return {
		dirtyFields,
		initialFieldStates,
		isInitialSetup,
		isFieldDirty,
		markFieldDirty,
		resetDirtyState,
		updateInitialStates,
		hasFieldChanged,
		markFieldDirtyIfChanged,
	};
};
